import type { Arrangement, Playbook, PlaybookGroup, Provision } from "../../services/lensService";

// A group, or Ungrouped (id null), which always comes last.
export interface Section {
    id: number | null;
    name: string;
    provisions: Provision[];
}

export const UNGROUPED = "Ungrouped";

export const sectionId = (id: number | null) => (id === null ? "ungrouped" : `group-${id}`);

export const sortGroups = (groups: PlaybookGroup[]) => [...groups].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);

// Stable: ties keep the API's Id order.
const byPlace = (provisions: Provision[]) =>
    provisions
        .map((provision, index) => ({ provision, index }))
        .sort((a, b) => a.provision.sortOrder - b.provision.sortOrder || a.index - b.index)
        .map((x) => x.provision);

// A provision whose group is unknown is shown as Ungrouped.
export const buildSections = (playbook: Pick<Playbook, "groups" | "provisions">): Section[] => {
    const groups = sortGroups(playbook.groups ?? []);
    const known = new Set(groups.map((g) => g.id));
    const provisions = playbook.provisions ?? [];
    const groupOf = (p: Provision) => (p.groupId != null && known.has(p.groupId) ? p.groupId : null);

    return [
        ...groups.map((g) => ({ id: g.id, name: g.name, provisions: byPlace(provisions.filter((p) => groupOf(p) === g.id)) })),
        { id: null, name: UNGROUPED, provisions: byPlace(provisions.filter((p) => groupOf(p) === null)) },
    ];
};

export const toArrangement = (sections: Section[]): Arrangement => ({
    groups: sections
        .filter((s) => s.id !== null)
        .map((s) => ({ id: s.id as number, provisionKeys: s.provisions.map((p) => p.key) })),
    ungrouped: sections.find((s) => s.id === null)?.provisions.map((p) => p.key) ?? [],
});

// Appends to the target group in display order; ones already there stay put.
export const moveTo = (sections: Section[], keys: Set<string>, targetId: number | null): Section[] => {
    const moving = sections.flatMap((s) => (s.id === targetId ? [] : s.provisions.filter((p) => keys.has(p.key))));
    return sections.map((s) =>
        s.id === targetId
            ? { ...s, provisions: [...s.provisions, ...moving] }
            : { ...s, provisions: s.provisions.filter((p) => !keys.has(p.key)) },
    );
};

// Optimistic copy of the playbook so the list does not jump back while the save runs.
export const applyArrangement = (playbook: Playbook, arrangement: Arrangement): Playbook => {
    const place = new Map<string, { groupId: number | null; sortOrder: number }>();
    arrangement.groups.forEach((g) => g.provisionKeys.forEach((key, i) => place.set(key, { groupId: g.id, sortOrder: i })));
    arrangement.ungrouped.forEach((key, i) => place.set(key, { groupId: null, sortOrder: i }));
    const groupOrder = new Map(arrangement.groups.map((g, i) => [g.id, i]));

    return {
        ...playbook,
        groups: playbook.groups.map((g) => ({ ...g, sortOrder: groupOrder.get(g.id) ?? g.sortOrder })),
        provisions: playbook.provisions.map((p) => ({ ...p, ...(place.get(p.key) ?? {}) })),
    };
};
